import { useState, useMemo } from 'react';
import {
  Container,
  Box,
  Flex,
  Button,
  Heading,
  Text,
  Select,
  Input,
  VStack,
  HStack,
  useDisclosure,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { useActionItems } from './useActionItems';
import { ActionItemListItem } from './components/ActionItemListItem';
import { AddActionItemModal } from './components/AddActionItemModal';
import { EditActionItemModal } from './components/EditActionItemModal';
import { DropActionItemModal } from './components/DropActionItemModal';
import { ActionItem } from '../types';

/**
 * Action Items Page - Main page for viewing and managing Action Items
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function ActionItemsPage() {
  const { currentUser } = useAuth();
  const {
    actionItems,
    users,
    rocks,
    issues,
    metrics,
    loading,
    createActionItem,
    updateActionItem,
    completeActionItem,
    uncompleteActionItem,
    dropActionItem,
    deleteActionItem,
  } = useActionItems(currentUser?.uid);

  const [filters, setFilters] = useState({
    status: 'all' as 'all' | 'complete' | 'incomplete',
    owner: 'all',
    search: '',
  });

  const [editingActionItem, setEditingActionItem] = useState<ActionItem | null>(null);
  const [droppingActionItem, setDroppingActionItem] = useState<ActionItem | null>(null);

  // Filter users to only those who have action items
  const usersWithActionItems = useMemo(() => {
    const activeOwnerIds = new Set(actionItems.map(item => item.ownerId).filter(Boolean));
    return users
      .filter(user => activeOwnerIds.has(user.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [actionItems, users]);

  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const {
    isOpen: isDropOpen,
    onOpen: onDropOpen,
    onClose: onDropClose,
  } = useDisclosure();

  const toast = useToast();

  const handleSaveActionItem = async (formData: any) => {
    try {
      await createActionItem(formData);
      toast({
        title: 'Action Item created',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onAddClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create action item',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleUpdateActionItem = async (formData: any) => {
    if (!editingActionItem) return;
    try {
      await updateActionItem(editingActionItem.id, formData);
      toast({
        title: 'Action Item updated',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onEditClose();
      setEditingActionItem(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update action item',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleToggleActionItem = async (actionItemId: string) => {
    const actionItem = actionItems.find((t) => t.id === actionItemId);
    if (!actionItem) return;

    try {
      if (actionItem.complete) {
        await uncompleteActionItem(actionItemId);
        toast({
          title: 'Action Item marked as incomplete',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        await completeActionItem(actionItemId);
        toast({
          title: 'Action Item completed',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : `Failed to ${actionItem.complete ? 'uncomplete' : 'complete'} action item`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDropActionItem = async (reason?: string) => {
    if (!droppingActionItem) return;
    try {
      await dropActionItem(droppingActionItem.id, reason);
      toast({
        title: 'Action Item dropped',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onDropClose();
      setDroppingActionItem(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to drop action item',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteActionItem = async (actionItemId: string) => {
    if (window.confirm('Are you sure you want to delete this action item?')) {
      try {
        await deleteActionItem(actionItemId);
        toast({
          title: 'Action Item deleted',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to delete action item',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleOpenEditModal = (actionItem: ActionItem) => {
    setEditingActionItem(actionItem);
    onEditOpen();
  };

  const handleOpenDropModal = (actionItem: ActionItem) => {
    setDroppingActionItem(actionItem);
    onDropOpen();
  };

  // Filter action items
  const filteredActionItems = actionItems.filter((actionItem) => {
    // Exclude action items associated with a Rock
    if (actionItem.rockId || actionItem.linkedRockId) return false;

    if (filters.status === 'complete' && !actionItem.complete) return false;
    if (filters.status === 'incomplete' && actionItem.complete) return false;
    if (filters.owner !== 'all' && actionItem.ownerId !== filters.owner) return false;
    if (
      filters.search &&
      !actionItem.description.toLowerCase().includes(filters.search.toLowerCase())
    )
      return false;
    return true;
  });

  const incompleteActionItems = filteredActionItems.filter((actionItem) => !actionItem.complete);
  const completeActionItems = filteredActionItems.filter((actionItem) => actionItem.complete);

  // Color mode values for dark mode support
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');
  const filterBg = useColorModeValue('white', 'gray.700');
  const emptyTextColor = useColorModeValue('gray.500', 'gray.400');

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text>Loading action items...</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={6}>
        <Flex justify="space-between" align="center" mb={4}>
          <Box>
            <Heading size="lg">Action Items</Heading>
            <Text color={subheadingColor} mt={1}>
              Track and manage your tasks
            </Text>
          </Box>
          <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={onAddOpen}>
            Add Action Item
          </Button>
        </Flex>

        <Box mb={6} p={4} bg={filterBg} borderRadius="md" shadow="sm">
          <Flex gap={4} wrap="wrap" align="center">
            <Box>
              <Text fontSize="sm" mb={1}>
                Status:
              </Text>
              <Select
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value as any })
                }
                size="sm"
                width="150px"
              >
                <option value="all">All</option>
                <option value="incomplete">Incomplete</option>
                <option value="complete">Complete</option>
              </Select>
            </Box>

            <Box>
              <Text fontSize="sm" mb={1}>
                Owner:
              </Text>
              <Select
                value={filters.owner}
                onChange={(e) => setFilters({ ...filters, owner: e.target.value })}
                size="sm"
                width="150px"
              >
                <option value="all">All Owners</option>
                {usersWithActionItems.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </Box>

            <Box flex={1} minW="200px">
              <Text fontSize="sm" mb={1}>
                Search:
              </Text>
              <Input
                placeholder="Search action items..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                size="sm"
              />
            </Box>

            {(filters.status !== 'all' || filters.owner !== 'all' || filters.search) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setFilters({ status: 'all', owner: 'all', search: '' })
                }
              >
                Clear Filters
              </Button>
            )}
          </Flex>
        </Box>
      </Box>

      <Tabs>
        <TabList>
          <Tab>Incomplete ({incompleteActionItems.length})</Tab>
          <Tab>Complete ({completeActionItems.length})</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            <VStack spacing={3} align="stretch">
              {incompleteActionItems.length === 0 ? (
                <Text color={emptyTextColor} textAlign="center" py={8}>
                  No incomplete action items
                </Text>
              ) : (
                incompleteActionItems.map((actionItem) => (
                  <ActionItemListItem
                    key={actionItem.id}
                    actionItem={actionItem}
                    users={users}
                    onEdit={() => handleOpenEditModal(actionItem)}
                    onDelete={() => handleDeleteActionItem(actionItem.id)}
                    onComplete={() => handleToggleActionItem(actionItem.id)}
                    onDrop={() => handleOpenDropModal(actionItem)}
                  />
                ))
              )}
            </VStack>
          </TabPanel>

          <TabPanel>
            <VStack spacing={3} align="stretch">
              {completeActionItems.length === 0 ? (
                <Text color={emptyTextColor} textAlign="center" py={8}>
                  No completed action items yet
                </Text>
              ) : (
                completeActionItems.map((actionItem) => (
                  <ActionItemListItem
                    key={actionItem.id}
                    actionItem={actionItem}
                    users={users}
                    onEdit={() => handleOpenEditModal(actionItem)}
                    onDelete={() => handleDeleteActionItem(actionItem.id)}
                    onComplete={() => handleToggleActionItem(actionItem.id)}
                    onDrop={() => handleOpenDropModal(actionItem)}
                  />
                ))
              )}
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <AddActionItemModal
        isOpen={isAddOpen}
        onClose={onAddClose}
        onSave={handleSaveActionItem}
        currentUserId={currentUser?.uid}
        rocks={rocks}
        issues={issues}
        metrics={metrics}
      />

      <EditActionItemModal
        isOpen={isEditOpen}
        onClose={() => {
          onEditClose();
          setEditingActionItem(null);
        }}
        onSave={handleUpdateActionItem}
        editingActionItem={editingActionItem}
        rocks={rocks}
        issues={issues}
        metrics={metrics}
      />

      <DropActionItemModal
        isOpen={isDropOpen}
        onClose={() => {
          onDropClose();
          setDroppingActionItem(null);
        }}
        onSave={handleDropActionItem}
        actionItem={droppingActionItem}
      />
    </Container>
  );
}
